import IconPlusLarge from 'central-icons/IconPlusLarge';
import { useState } from 'react';

import {
  ConfirmDialog,
  MenuItem,
  RevelioTooltip,
  Button as UiButton,
  useDisclosure,
} from '@revelio/ui';

import { CardListSelect } from '../../../../../shared/ui/card-list';
import { FilterId } from '../../../filters/types/filters';
import type { ReportParameterConfig } from '../../PageConfigProvider/page-reducer';
import { EditParameterModal } from './EditParameterModal';
import styles from './ParameterEditor.module.css';
import { ParamItemMenu } from './ParamItemMenu';
import { determinePrimaryParameter, toReadableFilterName } from './parameters';

export function ParameterEditor({
  parameters,
  onChange,
}: {
  parameters: ReportParameterConfig[];
  onChange: (next: ReportParameterConfig[]) => void;
}) {
  const usedFilterIds = new Set(parameters.map((p) => p.filterId));
  const changeParameters = (next: ReportParameterConfig[]) => {
    onChange(determinePrimaryParameter(next));
  };

  const allFilterIds = Object.values(FilterId);

  const {
    isOpen: isEditOpen,
    onOpen: onEditOpen,
    onClose: onEditClose,
  } = useDisclosure();
  const [editMode, setEditMode] = useState<'add' | 'edit'>('add');
  const [draftParam, setDraftParam] = useState<ReportParameterConfig | null>(
    null
  );
  const [pendingDelete, setPendingDelete] =
    useState<ReportParameterConfig | null>(null);

  const openAdd = () => {
    // pick first available filterId not used yet
    const available = allFilterIds.find((f) => !usedFilterIds.has(f));
    if (!available) return;
    const readable = toReadableFilterName(available);
    const parameter: ReportParameterConfig = {
      parameterId: crypto.randomUUID(),
      filterId: available,
      isPrimary: false,
      name: readable,
      selectionLimit: 1,
    };
    setEditMode('add');
    setDraftParam(parameter);
    onEditOpen();
  };

  const openEdit = (param: ReportParameterConfig) => {
    setEditMode('edit');
    setDraftParam(param);
    onEditOpen();
  };

  const updateParameter = (
    parameterId: string,
    updates: Partial<ReportParameterConfig>
  ) => {
    changeParameters(
      parameters.map((p) =>
        p.parameterId === parameterId ? { ...p, ...updates } : p
      )
    );
  };

  const removeParameter = (parameterId: string) => {
    changeParameters(parameters.filter((p) => p.parameterId !== parameterId));
  };

  return (
    <>
      <div className={styles.editorStack}>
        <div className={styles.editorHeader}>
          <span className={styles.editorTitle}>Parameters</span>
          <RevelioTooltip label="Add Parameter">
            <UiButton
              size="sm"
              color="secondary"
              variant="ghost"
              onClick={openAdd}
            >
              Add Parameter
            </UiButton>
          </RevelioTooltip>
        </div>
        <div className={styles.paramGrid}>
          {parameters.map((p) => (
            <CardListSelect<string>
              key={p.parameterId}
              id={p.parameterId}
              label={p.name}
              detail=" "
              variant="report"
              hideCheckbox={true}
              onChange={() => openEdit(p)}
              cta={
                <ParamItemMenu>
                  <MenuItem onClick={() => openEdit(p)}>Edit</MenuItem>
                  <MenuItem destructive onClick={() => setPendingDelete(p)}>
                    Delete
                  </MenuItem>
                </ParamItemMenu>
              }
            />
          ))}
          <UiButton
            fullWidth
            variant="outlined"
            className={styles.addCard}
            leftIcon={<IconPlusLarge size={16} />}
            onClick={openAdd}
          >
            Add
          </UiButton>
        </div>
      </div>

      {draftParam && (
        <EditParameterModal
          key={draftParam.parameterId}
          isOpen={isEditOpen}
          onClose={onEditClose}
          mode={editMode}
          initial={draftParam}
          onSubmit={(param) => {
            if (editMode === 'add') {
              changeParameters([...parameters, param]);
            } else {
              updateParameter(param.parameterId, {
                name: param.name,
                filterId: param.filterId,
                isPrimary: param.isPrimary,
                isOptional: param.isOptional,
                selectionLimit:
                  param.filterId === FilterId.DATE_RANGE
                    ? undefined
                    : param.selectionLimit,
              });
            }
          }}
        />
      )}

      {pendingDelete && (
        <ConfirmDialog
          open
          title="Remove Parameter?"
          confirmLabel="Remove"
          destructive
          onCancel={() => setPendingDelete(null)}
          onConfirm={() => {
            removeParameter(pendingDelete.parameterId);
            setPendingDelete(null);
          }}
        >
          Are you sure you want to remove {pendingDelete.name}?
        </ConfirmDialog>
      )}
    </>
  );
}
